import LocationsGeoCoder from '@apiClients/LocationsGeoCoder';

import { sellerLocationGeoCoderFactory } from '@apiClients/SellerLocationGeoCoderFactory';
import { Spinner } from '@components/Spinner';
import { DropOff, IDropOffPoint, ISenderAddressMarker } from '@packlink/cartography';
import { Address, IAddress, IDropOffAllParams, IPostalCodeCoordinates, PointType } from '@packlink/packlink-sdk';
import { getDropOffPoints } from '@store/actions';
import { PureComponent } from 'react';
import { withTranslation, WithTranslation } from '@packlink/translation-provider';

const icon = '/img/blue-marker.svg';
const hereMarker = '/img/here-marker.svg';

enum COLLAPSIBLE_STATE {
    CLOSED,
    WAITING_TO_OPEN,
    OPEN,
    ERROR,
}

interface ICollapsibleDropOffProps {
    serviceId: string | undefined;
    from: Address<IAddress> | undefined;
}

interface ICollapsibleState {
    collapsibleState: COLLAPSIBLE_STATE;
    dropOffPoints: IDropOffPoint[];
    senderAddressMarker?: ISenderAddressMarker;
}

const waitingState: ICollapsibleState = {
    collapsibleState: COLLAPSIBLE_STATE.WAITING_TO_OPEN,
    dropOffPoints: [],
};

const errorState: ICollapsibleState = {
    collapsibleState: COLLAPSIBLE_STATE.ERROR,
    dropOffPoints: [],
};

const initialState: ICollapsibleState = {
    collapsibleState: COLLAPSIBLE_STATE.CLOSED,
    dropOffPoints: [],
};

const emptyServiceLocation: IDropOffAllParams = {
    city: '',
    code: '',
    serviceId: '',
    zip: '',
};

type CollapsibleDropOffProps = ICollapsibleDropOffProps & WithTranslation;

class CollapsibleDropOff extends PureComponent<CollapsibleDropOffProps> {
    readonly state: ICollapsibleState = initialState;

    openMap = (): void => {
        const { collapsibleState } = this.state;
        const { from, serviceId } = this.props;

        if (collapsibleState !== COLLAPSIBLE_STATE.CLOSED) {
            this.closeMap();
        } else {
            this.setState(waitingState);

            getDropOffPoints(this.fromIPlaceToServiceLocation(from, serviceId))
                .then((points: IDropOffPoint[]) => {
                    this.setState({
                        collapsibleState: COLLAPSIBLE_STATE.OPEN,
                        dropOffPoints: points,
                    });

                    if (from) {
                        getSellerLatLng(from.street1, from.zipCode, from.city)
                            .then((postalCode: IPostalCodeCoordinates) => {
                                this.setState({
                                    senderAddressMarker: { ...postalCode, markerIcon: hereMarker },
                                });
                            })
                            .catch(() => {
                                this.setState({
                                    senderAddressMarker: undefined,
                                });
                            });
                    }
                })
                .catch(() => {
                    this.setState(errorState);
                });
        }
    };

    closeMap = (): void => {
        this.setState(initialState);
    };

    render(): JSX.Element | null {
        const { t } = this.props;
        const { collapsibleState, dropOffPoints } = this.state;

        const renderMap = (): JSX.Element | null => {
            if (collapsibleState === COLLAPSIBLE_STATE.CLOSED) {
                return null;
            }

            if (collapsibleState === COLLAPSIBLE_STATE.ERROR) {
                return (
                    <div className="drop-off-map--error">
                        <p>{t('services-table.error.drop-off-points')}</p>
                    </div>
                );
            }

            if (collapsibleState === COLLAPSIBLE_STATE.WAITING_TO_OPEN) {
                return (
                    <div className="drop-off-map-container">
                        <div className="drop-off-map-container__content">
                            <div className="spinner-container--center">
                                <Spinner />
                            </div>
                        </div>
                    </div>
                );
            }

            if (dropOffPoints.length === 0) {
                return <div className="drop-off-map--empty">{t('services-table.empty.drop-off-points')}</div>;
            }

            return this.renderDropOffMap(dropOffPoints);
        };

        return (
            <>
                <a
                    className="drop-off-map__open-link"
                    role="button"
                    aria-label={t('services-table.link.drop-off-aria-label')}
                    onClick={this.openMap}
                >
                    {t('services-table.link.drop-off-label')}
                </a>
                {renderMap()}
            </>
        );
    }

    private fromIPlaceToServiceLocation = (
        from: Address | undefined,
        serviceId: string | undefined,
    ): IDropOffAllParams => {
        if (!from || !serviceId) {
            return emptyServiceLocation;
        }

        return {
            city: from?.city || '',
            code: from?.alpha2Code || '',
            serviceId: serviceId || '',
            zip: from?.zipCode || '',
            pointsType: PointType.DROP_OFF,
        };
    };

    private renderDropOffMap = (points: IDropOffPoint[]): JSX.Element => {
        const { t } = this.props;

        return (
            <div className="drop-off-map-container" data-id="drop-off-map">
                <div className="drop-off-map-box">
                    <DropOff
                        addresses={points}
                        allowClose={true}
                        markerIcon={icon}
                        senderAddressMarker={this.state.senderAddressMarker}
                    />
                </div>
                <div className="drop-off-map-container__close">
                    <a
                        className="drop-off-map-container__close-button"
                        role="button"
                        aria-label={t('services-table.link.close-map-aria-label')}
                        onClick={this.closeMap}
                    >
                        {t('services-table.link.close-map-label')}
                    </a>
                </div>
            </div>
        );
    };
}

function getSellerLatLng(
    address: string | undefined,
    alphaCode: string,
    city: string,
): Promise<IPostalCodeCoordinates> {
    return sellerLocationGeoCoderFactory(new LocationsGeoCoder()).getSellerLocation(address, alphaCode, city);
}

export default withTranslation()(CollapsibleDropOff);
